import React, { useEffect, useState } from "react";
import { MapPin, Phone, Mail, Heart, Leaf, Users, ArrowRight, CheckCircle, Star, Calendar, Video, Package, BookOpen, Shield, Sparkles, MessageCircle, Brain, Target, Bed, Apple, Flower2, Home, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import NavBar from '@/components/NavBar';
import { NewsletterSignup } from '@/components/NewsletterSignup';
import { ReturnToTop } from '@/components/ReturnToTop';
import ScrollDownArrow from '@/components/ScrollDownArrow';
import { BOOKING_URL } from '@/constants/urls';
import OnlineConsultImageWebP from '@/assets/online-chinese-medicine-consult-women-and-the-way.webp';
import OnlineConsultImagePNG from '@/assets/online-chinese-medicine-consult-women-and-the-way.png';
import HerbalMedicineImageWebP from '@/assets/herbal-medicine-online-australia-women-and-the-way.webp';
import HerbalMedicineImagePNG from '@/assets/herbal-medicine-online-australia-women-and-the-way.png';

const OnlineConsultationsPage = () => {
  // Force scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Add dynamic meta tags and structured data
  useEffect(() => {
    // Update document title and meta tags
    document.title = "Online Chinese Medicine Consults Australia | Women and the Way";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Can't get to clinic? Receive Chinese medicine care, herbal prescriptions, and personalised support from anywhere in Australia. Book an online session.");
    }

    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', "Online Chinese Medicine Consults Australia | Women and the Way");
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', "Can't get to clinic? Receive Chinese medicine care, herbal prescriptions, and personalised support from anywhere in Australia. Book an online session.");
    }

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', "https://womenandtheway.com.au/online-consultations");
    }

    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      ogImage.setAttribute('content', "https://womenandtheway.com.au/online-chinese-medicine-consult-women-and-the-way.jpg");
    }

    // Update Twitter Card
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', "Online Chinese Medicine Consults Australia | Women and the Way");
    }

    // Add LocalBusiness Schema for online consultations
    const onlineSchema = {
      "@context": "https://schema.org",
      "@type": "HealthAndBeautyBusiness",
      "name": "Women and The Way - Online Chinese Medicine Consultations",
      "image": "https://womenandtheway.com.au/online-chinese-medicine-consult-women-and-the-way.jpg",
      "description": "Online Chinese medicine consultations, herbal prescriptions, and personalised support for women across Australia. Telehealth appointments available nationwide.",
      "url": "https://womenandtheway.com.au/online-consultations",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Currimundi",
        "addressRegion": "QLD",
        "addressCountry": "AU"
      },
      "priceRange": "$$",
      "telephone": "+61415537784",
      "email": "womenandtheway@gmail.com",
      "sameAs": [
        "https://www.facebook.com/Womenandtheway",
        "https://www.instagram.com/women_and_the_way"
      ],
      "serviceType": "Online Chinese Medicine Consultations",
      "areaServed": "AU"
    };

    // Remove existing schema if any
    const existingSchema = document.querySelector('script[type="application/ld+json"]');
    if (existingSchema) {
      existingSchema.remove();
    }

    // Add new schema
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(onlineSchema);
    document.head.appendChild(script);

    // Cleanup function
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  const handleBooking = () => {
    window.open(BOOKING_URL, '_blank');
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-white via-white to-amber-50">
        <NavBar />
        
        {/* Hero Section */}
        <section className="relative py-20 px-4 md:px-6">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                Online Chinese Medicine Consults
              </h1>
              <p className="text-xl md:text-2xl text-sage-600 mb-4 font-medium">
                Personalised care, wherever you are.
              </p>
            </div>

            {/* Hero Image */}
            <div className="mb-12">
              <picture>
                <source srcSet={OnlineConsultImageWebP} type="image/webp" />
                <img 
                  src={OnlineConsultImagePNG}
                  alt="Online Chinese Medicine Consultation" 
                  className="w-full h-64 md:h-80 object-cover rounded-2xl shadow-lg"
                />
              </picture>
            </div>

            {/* Main Content */}
            <div className="bg-white rounded-2xl shadow-lg border-2 border-amber-200/50 p-8 md:p-12 mb-8">
              <p className="text-lg text-sage-700 mb-8 leading-relaxed">
                Whether you're in a rural town, caring for a newborn, or simply unable to get to our Currimundi clinic - our online consultations offer the same deeply considered, compassionate care from the comfort of your home.
              </p>
              
              <p className="text-lg text-sage-700 mb-8 leading-relaxed">
                At Women and the Way, we support your health across every season of life. With online access to Traditional Chinese Medicine, you can receive expert guidance, custom herbal prescriptions, and emotional support - no matter where you are in Australia. Whether you're seeking <a href="/fertility-support" className="text-sage-600 hover:text-sage-800 underline transition-colors">Fertility Support</a>, <a href="/postpartum" className="text-sage-600 hover:text-sage-800 underline transition-colors">Postpartum Care</a>, or <a href="/emotional-healing" className="text-sage-600 hover:text-sage-800 underline transition-colors">Emotional Healing</a>, our telehealth sessions bring expert care to you, wherever you are in Australia.
              </p>
            </div>

            {/* Why Choose Online Care Section */}
            <div className="bg-gradient-to-br from-sage-50 to-amber-50 rounded-2xl border-2 border-sage-200/50 p-8 md:p-12 mb-8">
              <h2 className="font-serif text-3xl font-semibold mb-6 flex items-center gap-2 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                <Shield className="w-7 h-7 text-sage-600" />
                Why Choose Online Care?
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="border-amber-200/30 hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <Users className="w-6 h-6 text-amber-600 mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold text-sage-800 mb-2">One-on-one time with your practitioner</h3>
                        <p className="text-sage-700">Our telehealth sessions are spacious, calm, and confidential - designed to help you feel truly heard and supported.</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-amber-200/30 hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <Leaf className="w-6 h-6 text-sage-600 mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold text-sage-800 mb-2">Customised Chinese herbal prescriptions</h3>
                        <p className="text-sage-700">We craft a formula tailored to your symptoms and constitutional pattern, then send it directly to your door or have it ready for clinic pickup.</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-amber-200/30 hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <BookOpen className="w-6 h-6 text-amber-500 mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold text-sage-800 mb-2">Education and tools to use at home</h3>
                        <p className="text-sage-700">You'll receive practical guidance on diet, acupressure, rituals, supplements, and lifestyle - rooted in your unique presentation.</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-amber-200/30 hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <Heart className="w-6 h-6 text-sage-600 mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold text-sage-800 mb-2">A safe space for emotional care</h3>
                        <p className="text-sage-700">Chinese medicine doesn't separate mind and body. We offer gentle support for grief, stress, anxiety, trauma, and life transitions.</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* What We Support Section */}
            <div className="bg-white rounded-2xl shadow-lg border-2 border-amber-200/50 p-8 md:p-12 mb-8">
              <h2 className="font-serif text-3xl font-semibold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                What We Support Online
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-sage-600 mt-1 flex-shrink-0" />
                  <p className="text-sage-700">Irregular cycles, PMS & hormonal imbalance</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-sage-600 mt-1 flex-shrink-0" />
                  <p className="text-sage-700">Fertility planning & IVF support</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-sage-600 mt-1 flex-shrink-0" />
                  <p className="text-sage-700">Pregnancy & postnatal care</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-sage-600 mt-1 flex-shrink-0" />
                  <p className="text-sage-700">Perimenopause & menopause transitions</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-sage-600 mt-1 flex-shrink-0" />
                  <p className="text-sage-700">Digestive issues, bloating, constipation</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-sage-600 mt-1 flex-shrink-0" />
                  <p className="text-sage-700">Acne, skin conditions & stress-related breakouts</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-sage-600 mt-1 flex-shrink-0" />
                  <p className="text-sage-700">Sleep issues, anxiety & burnout</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-sage-600 mt-1 flex-shrink-0" />
                  <p className="text-sage-700">Emotional healing, grief & trauma recovery</p>
                </div>
              </div>
              <p className="text-sage-700 mt-6 italic">
                You don't need a diagnosis to book. If you're feeling off, we can help make sense of what your body is telling you.
              </p>
            </div>

            {/* How It Works Section */}
            <div className="bg-gradient-to-br from-sage-50 to-amber-50 rounded-2xl border-2 border-sage-200/50 p-8 md:p-12 mb-8">
              <h2 className="font-serif text-3xl font-semibold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                How It Works
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-amber-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-semibold text-sm flex-shrink-0 mt-1">
                      1
                    </div>
                    <div>
                      <h3 className="font-semibold text-sage-800 mb-2">Book your consult online</h3>
                      <p className="text-sage-700">Choose a time that suits you - initial or follow-up</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="bg-amber-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-semibold text-sm flex-shrink-0 mt-1">
                      2
                    </div>
                    <div>
                      <h3 className="font-semibold text-sage-800 mb-2">Connect via phone or video</h3>
                      <p className="text-sage-700">We'll guide the session just like we would in-clinic, using tongue photos, cycle charts, symptom patterns and conversation</p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-amber-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-semibold text-sm flex-shrink-0 mt-1">
                      3
                    </div>
                    <div>
                      <h3 className="font-semibold text-sage-800 mb-2">Receive your custom care plan</h3>
                      <p className="text-sage-700">We'll post your herbs, teas, flower essences or tonics, and send you any relevant tools or instructions</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="bg-amber-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-semibold text-sm flex-shrink-0 mt-1">
                      4
                    </div>
                    <div>
                      <h3 className="font-semibold text-sage-800 mb-2">Follow up and adjust</h3>
                      <p className="text-sage-700">We'll check in to see how you're feeling and adjust your care plan as needed</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Ideal For Section */}
            <div className="bg-white rounded-2xl shadow-lg border-2 border-amber-200/50 p-8 md:p-12 mb-8">
              <h2 className="font-serif text-3xl font-semibold text-sage-800 mb-6">
                Ideal For:
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-amber-600 text-xs font-semibold">1</span>
                  </div>
                  <p className="text-sage-700">New mothers and postpartum recovery</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-amber-600 text-xs font-semibold">2</span>
                  </div>
                  <p className="text-sage-700">Women living in rural or regional areas</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-amber-600 text-xs font-semibold">3</span>
                  </div>
                  <p className="text-sage-700">Clients across Australia wanting long-distance support</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-amber-600 text-xs font-semibold">4</span>
                  </div>
                  <p className="text-sage-700">Sensitive or introverted women seeking care from a familiar, comfortable space</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-amber-600 text-xs font-semibold">5</span>
                  </div>
                  <p className="text-sage-700">Those navigating chronic illness, fatigue or mental health challenges</p>
                </div>
              </div>
            </div>

            {/* Images Section */}
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div className="bg-white rounded-2xl shadow-lg border-2 border-amber-200/30 overflow-hidden">
                <picture>
                  <source srcSet={OnlineConsultImageWebP} type="image/webp" />
                  <img 
                    src={OnlineConsultImagePNG}
                    alt="Woman receiving online Chinese medicine consultation in a calm home environment"
                    className="w-full h-64 object-cover"
                  />
                </picture>
                <div className="p-6">
                  <p className="text-sage-700 text-sm">Comfortable telehealth consultations from anywhere in Australia</p>
                </div>
              </div>
              
              <div className="bg-white rounded-2xl shadow-lg border-2 border-amber-200/30 overflow-hidden">
                <picture>
                  <source srcSet={HerbalMedicineImageWebP} type="image/webp" />
                  <img 
                    src={HerbalMedicineImagePNG}
                    alt="Custom herbal formulas for online clients of Women and the Way"
                    className="w-full h-64 object-cover"
                  />
                </picture>
                <div className="p-6">
                  <p className="text-sage-700 text-sm">Custom herbal prescriptions delivered to your door</p>
                </div>
              </div>
            </div>

            {/* CTA Section */}
            <div className="bg-sage-800 rounded-2xl p-8 md:p-12 text-center">
              <h2 className="font-serif text-3xl font-bold text-white mb-4">
                Book Your Online Consultation
              </h2>
              <p className="text-lg text-sage-100 mb-8">
                You don't need to travel to feel cared for. We'll meet you where you are - physically, emotionally, energetically.
              </p>
              
              <div className="flex flex-col md:flex-row gap-4 justify-center items-center mb-6">
                <Button 
                  size="lg"
                  onClick={handleBooking}
                  className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-4 text-lg font-semibold rounded-full"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Book now at womenandtheway.com.au
                </Button>
              </div>
              
              <div className="bg-sage-700/50 rounded-lg p-4">
                <div className="grid md:grid-cols-3 gap-4 text-sm text-sage-200">
                  <div className="flex items-center justify-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>Appointments available Australia-wide</span>
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <Package className="w-4 h-4" />
                    <span>Herbs & remedies shipped or collected locally</span>
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <Video className="w-4 h-4" />
                    <span>Phone & video consults with Dr Kirby Quin (TCM)</span>
                  </div>
                </div>
                <p className="text-sm text-sage-200 mt-4">
                  Because true care should be accessible, no matter your postcode.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
      <ScrollDownArrow />
      <ReturnToTop />
    </>
  );
};

export default OnlineConsultationsPage; 